import {
    useCallback,
    useMemo,
    useState,
} from 'react';

import Weapons from './Weapons';
import Armor from './Armor';
import MagicItems from './MagicItems';
import Miscellaneous from './Miscellaneous';
import Treasure from './Treasure';

import Stats from './Stats';
import { WeightUnit } from '../../../components/dnd/inventory/Weight';
import './Equipment.css';

type EquipmentProps = {
    className?: string;
    characterId: number;
    maxCarryWeight?: number;
    weapons: any[];
    armor: any[];
    magicItems: any[];
    miscellaneous: any[];
    currency: { cp: number; sp: number; ep: number; gp: number; pp: number };
    treasure: any[];
};

function Equipment({
    className,
    characterId,
    weapons,
    armor,
    magicItems,
    miscellaneous,
    currency,
    treasure,
    maxCarryWeight,
}: EquipmentProps) {
    const getWeight = (items: any[]) => items.reduce((total, item) => total + item.weight * item.quantity, 0);
    const [weaponsWeight, setWeaponsWeight] = useState(() => getWeight(weapons));
    const [miscellaneousWeight, setMiscellaneousWeight] = useState(() => getWeight(miscellaneous));
    const [treasureWeight, setTreasureWeight] = useState(() => getWeight(treasure));
    const [weightUnit, setWeightUnit] = useState<WeightUnit>('lb');

    const handleWeaponsWeightChange = useCallback((nextWeight: number) => setWeaponsWeight(nextWeight), []);
    const handleMiscellaneousWeightChange = useCallback((nextWeight: number) => setMiscellaneousWeight(nextWeight), []);
    const handleTreasureWeightChange = useCallback((nextWeight: number) => setTreasureWeight(nextWeight), []);

    const weight = useMemo(() => {
        return getWeight(armor.concat(magicItems)) + weaponsWeight + miscellaneousWeight + treasureWeight;
    }, [armor, magicItems, weaponsWeight, miscellaneousWeight, treasureWeight]);

    return (
        <section className={className} aria-label="Equipment">
            <div className="equipment-summary">
                <Stats
                    weight={weight}
                    weightLimit={maxCarryWeight}
                    weightUnit={weightUnit}
                    onWeightUnitChange={setWeightUnit}
                />
                <div className="equipment-summary__attunement">
                    <span>Attuned</span>
                    <strong>0 / 3</strong>
                </div>
            </div>

            <div className="equipment-grid">
                <section className="equipment-section equipment-section--weapons">
                    <Weapons
                        characterId={characterId}
                        items={weapons}
                        onWeightChange={handleWeaponsWeightChange}
                        weightUnit={weightUnit}
                    />
                </section>

                <section className="equipment-section equipment-section--armor">
                    <Armor
                        items={[]}
                    />
                </section>

                <section className="equipment-section equipment-section--magic-items">
                    <MagicItems
                        items={magicItems}
                    />
                </section>

                <section className="equipment-section equipment-section--miscellaneous">
                    <Miscellaneous
                        characterId={characterId}
                        items={miscellaneous}
                        onWeightChange={handleMiscellaneousWeightChange}
                        weightUnit={weightUnit}
                    />
                </section>

                <section className="equipment-section equipment-section--treasure">
                    <Treasure
                        characterId={characterId}
                        currency={currency}
                        items={treasure}
                        onWeightChange={handleTreasureWeightChange}
                        weightUnit={weightUnit}
                    />
                </section>
            </div>
        </section>
    );
}

export default Equipment;
